import { Link } from '@tanstack/react-router';
import { MailCheck } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors } from '@nucleus-commerce/admin-kit/errors';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { toMagicLinkRequest } from '../api/mappers';
import { AuthPage } from '../components/auth-page';
import { ServerError } from '../components/server-error';
import { useRequestMagicLink } from '../hooks/use-request-magic-link';
import { magicLinkRequestSchema, type MagicLinkRequestValues } from '../schemas/two-factor-schemas';

/**
 * Emails a one-time sign-in link. The backend answers 202 whether or not the address has an
 * account, so the confirmation is neutral; 429 (five a minute per IP) is the visible failure.
 */
export function MagicLinkRequestPage() {
  const { t } = useTranslation('auth');
  const request = useRequestMagicLink();
  const [pickOrganization, setPickOrganization] = useState(false);
  const form = useForm<MagicLinkRequestValues>({
    resolver: useI18nResolver(magicLinkRequestSchema),
    defaultValues: { email: '', organizationSlug: '' },
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError);
  const backToSignIn = <Link to="/login">{t('links.backToSignIn')}</Link>;

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await request.mutateAsync(toMagicLinkRequest(values));
    } catch (error) {
      applyFieldErrors(error);
    }
  });

  if (request.isSuccess) {
    return (
      <AuthPage title={t('magicLink.sentTitle')} footer={backToSignIn}>
        <Alert role="status">
          <MailCheck aria-hidden />
          <AlertTitle>{t('magicLink.sent')}</AlertTitle>
          <AlertDescription>{t('magicLink.sentHint')}</AlertDescription>
        </Alert>
      </AuthPage>
    );
  }

  const rateLimit =
    isApiError(request.error) && request.error.kind === 'rate_limited' ? request.error.meta : null;
  const retryAfter =
    typeof rateLimit?.retryAfterSeconds === 'number' ? rateLimit.retryAfterSeconds : null;

  return (
    <AuthPage
      title={t('magicLink.title')}
      description={t('magicLink.description')}
      footer={backToSignIn}
    >
      <Form {...form}>
        <form onSubmit={onSubmit} className="flex flex-col gap-5" noValidate>
          {rateLimit ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>
                {retryAfter === null
                  ? t('magicLink.rateLimitedGeneric')
                  : t('magicLink.rateLimited', { count: retryAfter })}
              </AlertDescription>
            </Alert>
          ) : request.isError && !request.error.hasFieldErrors ? (
            <ServerError error={request.error} />
          ) : null}
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('fields.email')}</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    autoComplete="email"
                    autoFocus
                    placeholder={t('fields.emailPlaceholder')}
                    className="h-11"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {pickOrganization ? (
            <FormField
              control={form.control}
              name="organizationSlug"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.organizationSlug')}</FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="organization"
                      autoCapitalize="off"
                      spellCheck={false}
                      placeholder={t('fields.organizationSlugPlaceholder')}
                      className="h-11"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>{t('magicLink.organizationHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="self-start px-0"
              onClick={() => setPickOrganization(true)}
            >
              {t('magicLink.severalOrganizations')}
            </Button>
          )}
          <Button type="submit" size="lg" className="h-11 w-full" disabled={request.isPending}>
            {request.isPending ? t('magicLink.submitting') : t('magicLink.submit')}
          </Button>
        </form>
      </Form>
    </AuthPage>
  );
}
